import { useCallback, useEffect, useState } from 'react';
import { useQuery } from 'urql';

import { CreatorDiscoveryInstagramAccount } from '@frontend/app/hooks';
import { DiscoverInstagramCreatorsQuery } from '@frontend/gql/social/graphql';

import { QueryVariables } from '../../types';
import { discoverInstagramCreators, getInstagramCreator } from '../graphql';

export const useInstagramCreatorSearchData = (
  queryVariables: QueryVariables,
  clientId: string,
  selectedInstagramAccount?: CreatorDiscoveryInstagramAccount,
  defaultFiltersResolved?: boolean,
) => {
  const [searchResults, setSearchResults] = useState<DiscoverInstagramCreatorsQuery | null>(null);
  const [refetching, setRefetching] = useState(false);
  const [isPaginationFetching, setIsPaginationFetching] = useState(false);
  const { igUserId } = selectedInstagramAccount ?? {};

  // Determine which query to use based on search context
  const isUsernameSearch = queryVariables.searchContext === 'username';

  // Variables for discoverInstagramCreators query
  const discoverVariables = {
    ...(() => {
      const { searchContext: _searchContext, username: _username, creatorUsername: _creatorUsername, ...graphqlVariables } = queryVariables;
      return graphqlVariables;
    })(),
    igUserId: igUserId || '',
    clientId,
  };

  // Variables for getInstagramCreator query
  const getCreatorVariables = {
    clientId,
    igUserId: igUserId || '',
    username: queryVariables.username || '',
  };

  // Variables for similar creators when doing username search
  const similarCreatorsVariables = {
    similarToCreators: [queryVariables.username || queryVariables.creatorUsername || ''],
    igUserId: igUserId || '',
    clientId,
  };

  // Use discoverInstagramCreators query for non-username searches
  const [{ data: discoverData, fetching: discoverFetching, error: discoverError }, refetchDiscover] = useQuery({
    query: discoverInstagramCreators,
    variables: discoverVariables,
    pause: !igUserId || defaultFiltersResolved === false || isUsernameSearch,
  });

  // Use getInstagramCreator query for username searches
  const [{ data: creatorData, fetching: creatorFetching, error: creatorError }, refetchCreator] = useQuery({
    query: getInstagramCreator,
    variables: getCreatorVariables,
    pause: !igUserId || defaultFiltersResolved === false || !isUsernameSearch || !queryVariables.username?.trim(),
  });

  // Fetch similar creators when doing username search
  const [{ data: similarCreatorsData, fetching: similarCreatorsFetching, error: similarCreatorsError }, refetchSimilarCreators] = useQuery({
    query: discoverInstagramCreators,
    variables: similarCreatorsVariables,
    pause: !igUserId || defaultFiltersResolved === false || (!isUsernameSearch || !queryVariables.username?.trim()) && !queryVariables.creatorUsername?.trim(),
  });

  // Combine fetching states
  const fetching = isUsernameSearch ? (creatorFetching || similarCreatorsFetching) : discoverFetching || (queryVariables.creatorUsername?.trim() ? similarCreatorsFetching : false);
  const error = isUsernameSearch ? (creatorError || similarCreatorsError) : discoverError;
  const data = isUsernameSearch ? creatorData : discoverData;

  useEffect(() => {
    if (!fetching) {
      setRefetching(false);
      setIsPaginationFetching(false);
    }
  }, [fetching]);

  // Process query results
  useEffect(() => {
    if (isUsernameSearch && queryVariables.username?.trim() && creatorData?.getInstagramCreator?.creator) {
      // Transform single creator result to match expected array format
      const transformedData: DiscoverInstagramCreatorsQuery = {
        discoverInstagramCreators: {
          creators: [creatorData.getInstagramCreator.creator],
          paging: {
            cursors: {
              after: null,
              before: null,
            },
            hasNext: false,
            hasPrevious: false,
          },
        },
      };
      setSearchResults(transformedData);
    } else if (!isUsernameSearch && discoverData?.discoverInstagramCreators?.creators) {
      setSearchResults(discoverData);
    } else if (isUsernameSearch && !queryVariables.username?.trim()) {
      // Clear search results when username is cleared
      setSearchResults(null);
    } else if (!isUsernameSearch && !discoverData?.discoverInstagramCreators?.creators) {
      // Clear search results when switching away from username search and no discover data
      setSearchResults(null);
    }
  }, [discoverData, creatorData, queryVariables.username, isUsernameSearch]);

  // Handle search submission
  const handleRefresh = useCallback(() => {
    setRefetching(true);
    if (isUsernameSearch) {
      refetchCreator({ requestPolicy: 'network-only' });
      refetchSimilarCreators({ requestPolicy: 'network-only' });
    } else {
      refetchDiscover({ requestPolicy: 'network-only' });
    }

    // Also refetch similar creators if creatorUsername exists
    if (queryVariables.creatorUsername?.trim()) {
      refetchSimilarCreators({ requestPolicy: 'network-only' });
    }
  }, [refetchDiscover, refetchCreator, refetchSimilarCreators, isUsernameSearch, queryVariables.creatorUsername]);

  // Handle pagination (not applicable for single creator search)
  const handlePagination = useCallback(() => {
    if (!isUsernameSearch) {
      setIsPaginationFetching(true);
      refetchDiscover({ requestPolicy: 'network-only' });
    }
  }, [refetchDiscover, isUsernameSearch]);

  return {
    data,
    fetching: fetching || defaultFiltersResolved === false, // Show loading while waiting for default filters
    error,
    searchResults,
    similarCreatorsData: (isUsernameSearch || queryVariables.creatorUsername?.trim()) ? similarCreatorsData?.discoverInstagramCreators?.creators : null,
    handleSearch: handleRefresh,
    handlePagination,
    refetch: isUsernameSearch ? refetchCreator : refetchDiscover,
    refetching,
    isPaginationFetching,
  };
};
